import React from 'react';
import { Helmet } from 'react-helmet-async';
import { ArrowRight, CheckCircle2, Anchor, FileText, Shield, TrendingUp, Zap, BarChart2 } from 'lucide-react';

const Maritime = () => {
  const useCases = [
    {
      icon: Anchor,
      title: 'Vessel Performance & Fuel Optimisation',
      description: 'ML models that analyse AIS data, weather routing, and engine telemetry to recommend optimal speed and trim — cutting fuel consumption and emissions.',
      outcomes: ['8–15% fuel cost reduction', 'CII rating improvement', 'Real-time voyage optimisation'],
    },
    {
      icon: FileText,
      title: 'Ship Chandler & Procurement Automation',
      description: 'AI-powered RFQ automation, supplier comparison, and purchase order processing for ship supplies — eliminating manual back-and-forth at port.',
      outcomes: ['70% reduction in procurement cycle time', 'Automated supplier benchmarking', 'Full procurement audit trail'],
    },
    {
      icon: TrendingUp,
      title: 'Predictive Maintenance for Vessels',
      description: 'IoT sensor data analytics that predict equipment failures before they cause unplanned downtime — keeping vessels in service and crews safe.',
      outcomes: ['40% reduction in unplanned downtime', 'Condition-based maintenance scheduling', 'Spare parts inventory optimisation'],
    },
    {
      icon: BarChart2,
      title: 'Port Operations & Berth Scheduling AI',
      description: 'Predictive berth planning, yard optimisation, and vessel arrival forecasting that reduces port congestion and turnaround time.',
      outcomes: ['Reduced vessel waiting time', 'Optimised crane and labour allocation', 'Real-time port congestion alerts'],
    },
    {
      icon: Shield,
      title: 'Maritime Compliance & Flag State Reporting',
      description: 'Automated IMO compliance monitoring, MARPOL reporting, and CII / EEXI calculation — removing the burden of manual regulatory submissions.',
      outcomes: ['Automated CII/EEXI reporting', 'MARPOL compliance tracking', 'PSC inspection readiness scoring'],
    },
    {
      icon: Zap,
      title: 'Cargo & Charter Intelligence',
      description: 'AI market intelligence for freight rate forecasting, charter party analytics, and cargo matching — giving chartering teams a data edge.',
      outcomes: ['Freight rate trend forecasting', 'Automated charter party review', 'Cargo-to-vessel matching'],
    },
  ];

  const caseStudies = [
    {
      title: 'Ship Chandler RFQ Automation',
      result: 'Automated the end-to-end RFQ process for a global ship chandler, cutting response time from 48 hours to under 2 hours.',
      link: '/case-studies/ship-chandler-rfq',
    },
    {
      title: 'Supply Chain & Logistics AI',
      result: 'Real-time visibility platform across 200 vessel voyages, reducing fuel variance by 12% through AI-optimised routing.',
      link: '/case-studies/retail-analytics',
    },
  ];

  const techStack = [
    'AIS data integration', 'IoT sensor pipelines', 'Azure IoT Hub', 'Python / TensorFlow',
    'Weather API integrations (ECMWF, DTN)', 'SAP MM / Oracle Procurement connectors',
    'Power BI maritime dashboards', 'GPT-4 for document intelligence',
  ];

  return (
    <>
      <Helmet>
        <title>AI for Maritime | Vessel Performance, Procurement & Port Operations AI | Agility</title>
        <meta
          name="description"
          content="AI solutions for the maritime industry. Vessel performance optimisation, ship chandler automation, predictive maintenance, port operations AI, and maritime compliance. Production systems in 3–8 weeks."
        />
        <meta
          name="keywords"
          content="maritime AI, vessel performance AI, ship chandler automation, port operations AI, maritime predictive maintenance, shipping AI, CII compliance AI"
        />
        <link rel="canonical" href="https://agility.ai/industries/maritime" />
        <meta property="og:title" content="AI for Maritime | Agility" />
        <meta property="og:description" content="AI solutions for maritime: vessel optimisation, procurement automation, and port intelligence." />
        <meta property="og:type" content="website" />
      </Helmet>

      {/* Hero */}
      <section className="relative pt-40 pb-24 bg-black overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-br from-teal-900/20 to-black pointer-events-none" />
        <div className="container mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
          <div className="max-w-4xl">
            <div className="inline-flex items-center bg-teal-500/10 border border-teal-500/20 rounded-full px-4 py-2 mb-6">
              <span className="text-teal-400 text-sm font-medium">Industry Solution</span>
            </div>
            <h1 className="text-4xl md:text-6xl font-bold text-white mb-6 leading-tight">
              AI for <span className="text-teal-400">Maritime</span>
            </h1>
            <p className="text-xl text-gray-300 mb-8 max-w-2xl leading-relaxed">
              We build AI systems that optimise vessel performance, automate maritime procurement, and bring intelligence to port and fleet operations.
            </p>
            <div className="flex flex-col sm:flex-row gap-4">
              <a
                href="/contact"
                className="inline-flex items-center bg-teal-500 text-white px-8 py-4 rounded-md hover:bg-teal-400 transition-colors font-semibold text-lg"
              >
                Talk to a Maritime AI Specialist <ArrowRight className="ml-2" size={20} />
              </a>
              <a
                href="/case-studies"
                className="inline-flex items-center border border-gray-600 text-white px-8 py-4 rounded-md hover:border-teal-400 hover:text-teal-400 transition-colors font-semibold text-lg"
              >
                View Case Studies
              </a>
            </div>
          </div>
        </div>
      </section>

      {/* Use Cases */}
      <section className="py-24 bg-gray-950">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-3xl md:text-4xl font-bold text-white mb-4">AI Use Cases for Maritime</h2>
            <p className="text-gray-400 text-lg max-w-2xl mx-auto">
              From bridge to back-office — AI across the full maritime value chain.
            </p>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {useCases.map((useCase, index) => {
              const Icon = useCase.icon;
              return (
                <div key={index} className="bg-gray-900 rounded-xl p-6 border border-gray-800 hover:border-teal-500/40 transition-colors">
                  <div className="w-12 h-12 bg-teal-500/10 rounded-lg flex items-center justify-center mb-4">
                    <Icon className="text-teal-400" size={24} />
                  </div>
                  <h3 className="text-xl font-semibold text-white mb-3">{useCase.title}</h3>
                  <p className="text-gray-400 mb-4 text-sm leading-relaxed">{useCase.description}</p>
                  <ul className="space-y-2">
                    {useCase.outcomes.map((outcome, i) => (
                      <li key={i} className="flex items-start gap-2 text-sm text-gray-300">
                        <CheckCircle2 className="text-teal-400 mt-0.5 flex-shrink-0" size={14} />
                        {outcome}
                      </li>
                    ))}
                  </ul>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* Case Studies */}
      <section className="py-24 bg-black">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-3xl md:text-4xl font-bold text-white mb-12 text-center">Maritime Case Studies</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-8 max-w-4xl mx-auto">
            {caseStudies.map((cs, index) => (
              <a
                key={index}
                href={cs.link}
                className="block bg-gray-900 rounded-xl p-8 border border-gray-800 hover:border-teal-500/40 transition-colors group"
              >
                <h3 className="text-xl font-semibold text-white mb-3 group-hover:text-teal-400 transition-colors">{cs.title}</h3>
                <p className="text-gray-400 mb-4 text-sm">{cs.result}</p>
                <span className="text-teal-400 text-sm font-medium inline-flex items-center gap-1">
                  Read case study <ArrowRight size={14} />
                </span>
              </a>
            ))}
          </div>
        </div>
      </section>

      {/* Tech Stack */}
      <section className="py-24 bg-gray-950">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-3xl mx-auto text-center">
            <h2 className="text-3xl font-bold text-white mb-8">Technology Stack</h2>
            <div className="flex flex-wrap gap-3 justify-center">
              {techStack.map((tech, index) => (
                <span key={index} className="bg-gray-800 text-gray-300 px-4 py-2 rounded-full text-sm border border-gray-700">
                  {tech}
                </span>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-24 bg-teal-600">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-3xl md:text-4xl font-bold text-white mb-4">Ready to bring AI to your maritime operations?</h2>
          <p className="text-teal-100 text-lg mb-8 max-w-2xl mx-auto">
            Production-ready maritime AI in 3–8 weeks. Let's start with your biggest operational challenge.
          </p>
          <a
            href="/contact"
            className="inline-flex items-center bg-white text-teal-600 px-8 py-4 rounded-md hover:bg-gray-100 transition-colors font-semibold text-lg"
          >
            Book a Free Discovery Call <ArrowRight className="ml-2" size={20} />
          </a>
        </div>
      </section>
    </>
  );
};

export default Maritime;
